import React, { useEffect, useMemo, useRef, useState } from "react";
import { writeStoredSession } from "../utils/accountLinks";
import { POS_SEND_WHATSAPP_BILLING_ON_CREATE_ORDER_KEY } from "../utils/whatsappInvoice";
import { getCurrencySymbol, readStoredCurrencyContext } from "../utils/currency";
import {
  resolveTaxLabelFromProfile,
  resolveTaxRegistrationLabelFromProfile,
} from "../utils/taxDetails";

import { API_ENDPOINTS, API_BASE_URL, IMAGE_BASE_URL } from "../config/constants";
import PrinterSettingsPage from "./PrinterSettingsPage";
import { hasPOSLockPin, removePOSLockPin, savePOSLockPin, verifyPOSLockPin } from "../utils/posLock";
import {
  POS_SHOW_PRODUCT_IMAGES_KEY,
  POS_SHOW_PRODUCT_PRICES_KEY,
  POS_SHOW_PRODUCT_QUANTITY_BUTTONS_KEY,
  POS_SHOW_PRODUCT_QUANTITY_FIELD_KEY,
  readPOSDisplayPreference,
  writePOSDisplayPreference,
} from "../utils/posDisplayPreferences";
import { POS_CUSTOMER_COPY_KOT_NUMBER_KEY } from "../utils/kotPrint";
import {
  readHoldDockPreference,
  writeHoldDockPreference,
} from "../utils/holdDockPreference";
import {
  readAiManagerPreference,
  writeAiManagerPreference,
} from "../utils/aiManagerPreference";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { saveOfflineProfile } from "../offline/profileStore";

const BRAND = "var(--app-primary)";
const LIGHT = "#cceaff";
const ORDER_SEQUENCE_SETTINGS_URL =
  `${API_BASE_URL}/restaurant/order-sequence-settings`;
const CURRENCY_SYMBOL = getCurrencySymbol(readStoredCurrencyContext());
const STORED_PROFILE = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
const TAX_LABEL = resolveTaxLabelFromProfile(STORED_PROFILE);
const TAX_REGISTRATION_LABEL = resolveTaxRegistrationLabelFromProfile(STORED_PROFILE);

const POS_PREFERENCE_KEYS = {
  categoryLayout: "pos_category_layout",
  deliveryEnabled: "pos_delivery_enabled",
  deliveryCharge: "pos_delivery_charge",
  taxBreakupOnBill: "pos_show_tax_breakup_on_bill",
  deliveryCopyLogoEnabled: "pos_delivery_copy_logo_enabled",
  qrOrderAlertsEnabled: "qr_order_alerts_enabled",
  orderCreatedToneEnabled: "pos_order_created_tone_enabled",
  sendWhatsAppBillingOnCreateOrder:
    POS_SEND_WHATSAPP_BILLING_ON_CREATE_ORDER_KEY,
  customerCopyKotNumber: POS_CUSTOMER_COPY_KOT_NUMBER_KEY,
};

const POS_CATEGORY_LAYOUTS = {
  vertical: "vertical",
  horizontal: "horizontal",
};

const normalizeMoneyInput = (text) => {
  const cleaned = String(text || "").replace(/[^0-9.]/g, "");
  if (!cleaned) return "";
  const [whole, ...rest] = cleaned.split(".");
  const decimals = rest.join("").slice(0, 2);
  return decimals ? `${whole}.${decimals}` : whole;
};

const parseToggle = (value) =>
  value === true || value === 1 || value === "1" || String(value || "").toLowerCase() === "true";

const readProfileImage = (file) => new Promise((resolve, reject) => {
  if (!(file instanceof File)) return resolve("");
  if (file.size > 500 * 1024) {
    reject(new Error("Profile image must be smaller than 500 KB."));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ""));
  reader.onerror = () => reject(new Error("Unable to read the selected profile image."));
  reader.readAsDataURL(file);
});

const writeSequenceSetting = (session, profile, restaurantId, key, enabled) => {
  const nextValue = enabled ? 1 : 0;
  const nextProfile = {
    ...profile,
    [key]: nextValue,
    ...(profile?.restaurant_profile
      ? { restaurant_profile: { ...profile.restaurant_profile, [key]: nextValue } }
      : {}),
  };
  let nextSession = session?.data ? { ...session, data: nextProfile } : nextProfile;
  if (Array.isArray(session?.account_link_context?.accounts)) {
    nextSession = {
      ...nextSession,
      account_link_context: {
        ...session.account_link_context,
        accounts: session.account_link_context.accounts.map((account) =>
          String(account?.id || "") === String(restaurantId)
            ? { ...account, [key]: nextValue }
            : account
        ),
      },
    };
  }
  writeStoredSession(nextSession, { preserveAccountLinkContext: true, syncLogRocket: false });
  return nextProfile;
};

const POSSettingsPage = () => {
  const lockUser = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
  const lockRestaurantId = lockUser?.id || lockUser?.restaurant_id;
  const layoutOptions = useMemo(
    () => [
      {
        id: POS_CATEGORY_LAYOUTS.horizontal,
        title: "Side Categories",
        description: "Categories appear as a side rail with items on the right.",
        icon: "☰",
      },
      {
        id: POS_CATEGORY_LAYOUTS.vertical,
        title: "Top Categories",
        description: "Categories appear on top with a compact grid below.",
        icon: "▦",
      },
    ],
    []
  );

  const [selectedLayout, setSelectedLayout] = useState(
    POS_CATEGORY_LAYOUTS.vertical
  );
  const [showProductImages, setShowProductImages] = useState(() =>
    readPOSDisplayPreference(POS_SHOW_PRODUCT_IMAGES_KEY)
  );
  const [showProductPrices, setShowProductPrices] = useState(() =>
    readPOSDisplayPreference(POS_SHOW_PRODUCT_PRICES_KEY)
  );
  const [showProductQuantityButtons, setShowProductQuantityButtons] = useState(() =>
    readPOSDisplayPreference(POS_SHOW_PRODUCT_QUANTITY_BUTTONS_KEY)
  );
  const [showProductQuantityField, setShowProductQuantityField] = useState(() =>
    readPOSDisplayPreference(POS_SHOW_PRODUCT_QUANTITY_FIELD_KEY)
  );
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [deliveryCharge, setDeliveryCharge] = useState("0");
  const [showTaxBreakup, setShowTaxBreakup] = useState(true);
  const [showDeliveryLogo, setShowDeliveryLogo] = useState(false);
  const [customerCopyKotNumber, setCustomerCopyKotNumber] = useState(false);
  const [qrOrderAlertsEnabled, setQrOrderAlertsEnabled] = useState(true);
  const [orderCreatedToneEnabled, setOrderCreatedToneEnabled] = useState(true);
  const [sendWhatsAppBillingOnCreateOrder, setSendWhatsAppBillingOnCreateOrder] =
    useState(false);
  const [holdDockEnabled, setHoldDockEnabled] = useState(() =>
    readHoldDockPreference()
  );
  const [aiManagerEnabled, setAiManagerEnabled] = useState(() =>
    readAiManagerPreference()
  );
  const [dailyOrderSequenceResetEnabled, setDailyOrderSequenceResetEnabled] =
    useState(false);
  const [savingOrderSequenceSetting, setSavingOrderSequenceSetting] =
    useState(false);
  const [dailyKotSequenceResetEnabled, setDailyKotSequenceResetEnabled] =
    useState(false);
  const [savingKotSequenceSetting, setSavingKotSequenceSetting] =
    useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoChanged, setPhotoChanged] = useState(false);
  const [lockPinConfigured, setLockPinConfigured] = useState(() => hasPOSLockPin(lockRestaurantId));
  const [currentLockPin, setCurrentLockPin] = useState("");
  const [newLockPin, setNewLockPin] = useState("");
  const [confirmLockPin, setConfirmLockPin] = useState("");
  const [savingLockPin, setSavingLockPin] = useState(false);
  const [lockPinMessage, setLockPinMessage] = useState("");
  const fileInputRef = useRef(null);

  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pShopName, setPShopName] = useState("");
  const [pAddr1, setPAddr1] = useState("");
  const [pAddr2, setPAddr2] = useState("");
  const [pAddr3, setPAddr3] = useState("");
  const [pCf1, setPCf1] = useState("");
  const [pCf2, setPCf2] = useState("");
  const [pCf3, setPCf3] = useState("");
  const [pGst, setPGst] = useState("");
  const [pUpi, setPUpi] = useState("");
  const [pSound, setPSound] = useState(1);
  const [pVibration, setPVibration] = useState(1);
  const [pShowLogo, setPShowLogo] = useState(1);
  const [pShowQr, setPShowQr] = useState(1);
  const [pShowPhone, setPShowPhone] = useState(1);
  const [pShowThanks, setPShowThanks] = useState(1);

  const cleanPin = (value) => String(value || "").replace(/\D/g, "").slice(0, 6);
  const handleSaveLockPin = async () => {
    setLockPinMessage("");
    if (!/^\d{4,6}$/.test(newLockPin)) return setLockPinMessage("New PIN must be 4 to 6 digits.");
    if (newLockPin !== confirmLockPin) return setLockPinMessage("New PIN and confirmation do not match.");
    setSavingLockPin(true);
    try {
      if (lockPinConfigured && !await verifyPOSLockPin(lockRestaurantId, currentLockPin)) {
        setLockPinMessage("Current PIN is incorrect.");
        return;
      }
      await savePOSLockPin(lockRestaurantId, newLockPin);
      setLockPinConfigured(true);
      setCurrentLockPin("");
      setNewLockPin("");
      setConfirmLockPin("");
      setLockPinMessage("POS Lock PIN saved successfully.");
    } catch (error) {
      setLockPinMessage(error?.message || "Unable to save PIN.");
    } finally {
      setSavingLockPin(false);
    }
  };

  const handleRemoveLockPin = async () => {
    setLockPinMessage("");
    if (!await verifyPOSLockPin(lockRestaurantId, currentLockPin)) {
      setLockPinMessage("Enter the correct current PIN to remove POS Lock.");
      return;
    }
    removePOSLockPin(lockRestaurantId);
    setLockPinConfigured(false);
    setCurrentLockPin("");
    setNewLockPin("");
    setConfirmLockPin("");
    setLockPinMessage("POS Lock PIN removed.");
  };

  useEffect(() => {
    const savedLayout = localStorage.getItem(POS_PREFERENCE_KEYS.categoryLayout);
    const savedDeliveryEnabled = localStorage.getItem(
      POS_PREFERENCE_KEYS.deliveryEnabled
    );
    const savedDeliveryCharge = localStorage.getItem(
      POS_PREFERENCE_KEYS.deliveryCharge
    );
    const savedShowTaxBreakup = localStorage.getItem(
      POS_PREFERENCE_KEYS.taxBreakupOnBill
    );
    const savedDeliveryLogo = localStorage.getItem(
      POS_PREFERENCE_KEYS.deliveryCopyLogoEnabled
    );
    const savedCustomerCopyKotNumber = localStorage.getItem(
      POS_PREFERENCE_KEYS.customerCopyKotNumber
    );
    const savedQrOrderAlertsEnabled = localStorage.getItem(
      POS_PREFERENCE_KEYS.qrOrderAlertsEnabled
    );
    const savedOrderCreatedToneEnabled = localStorage.getItem(
      POS_PREFERENCE_KEYS.orderCreatedToneEnabled
    );
    const savedSendWhatsAppBillingOnCreateOrder = localStorage.getItem(
      POS_PREFERENCE_KEYS.sendWhatsAppBillingOnCreateOrder
    );

    if (savedLayout) setSelectedLayout(savedLayout);
    if (savedDeliveryEnabled !== null)
      setDeliveryEnabled(savedDeliveryEnabled === "true");
    if (savedDeliveryCharge !== null) setDeliveryCharge(savedDeliveryCharge);
    if (savedShowTaxBreakup !== null)
      setShowTaxBreakup(savedShowTaxBreakup === "true");
    if (savedDeliveryLogo !== null)
      setShowDeliveryLogo(savedDeliveryLogo === "true");
    setCustomerCopyKotNumber(savedCustomerCopyKotNumber === "true");

    setQrOrderAlertsEnabled(
      savedQrOrderAlertsEnabled === null
        ? true
        : savedQrOrderAlertsEnabled === "true"
    );
    setOrderCreatedToneEnabled(
      savedOrderCreatedToneEnabled === null
        ? true
        : savedOrderCreatedToneEnabled === "true"
    );
    setSendWhatsAppBillingOnCreateOrder(
      savedSendWhatsAppBillingOnCreateOrder === "true"
    );
    const storedSession = JSON.parse(localStorage.getItem("userData") || "{}");
    const storedProfile = storedSession?.data || storedSession || {};
    setDailyOrderSequenceResetEnabled(
      parseToggle(
        storedProfile?.daily_order_sequence_reset_enabled ??
          storedProfile?.restaurant_profile?.daily_order_sequence_reset_enabled
      )
    );
    setDailyKotSequenceResetEnabled(
      parseToggle(
        storedProfile?.daily_kot_sequence_reset_enabled ??
          storedProfile?.restaurant_profile?.daily_kot_sequence_reset_enabled
      )
    );
  }, []);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const u = stored?.data || stored || {};
    setPName(u?.name || "");
    setPEmail(u?.email || "");
    setPShopName(u?.restaurant_name || "");
    setPAddr1(u?.addressline1 || "");
    setPAddr2(u?.addressline2 || "");
    setPAddr3(u?.addressline3 || "");
    setPCf1(u?.customfield1 || "");
    setPCf2(u?.customfield2 || "");
    setPCf3(u?.customfield3 || "");
    setPGst(u?.gstno || "");
    setPUpi(u?.upiaddress || "");
    setPSound(typeof u?.issoundenable === "number" ? u.issoundenable : 1);
    setPVibration(typeof u?.isvibrationenable === "number" ? u.isvibrationenable : 1);
    setPShowLogo(typeof u?.showlogoonprinter === "number" ? u.showlogoonprinter : 1);
    setPShowQr(typeof u?.showqrcodeonprinter === "number" ? u.showqrcodeonprinter : 1);
    setPShowPhone(typeof u?.showphonenumberonprinter === "number" ? u.showphonenumberonprinter : 1);
    setPShowThanks(typeof u?.showthanksonbill === "number" ? u.showthanksonbill : 1);
    let avatarUrl = u?.avatar_url || u?.photo || u?.logo || u?.restaurant_logo || "";
    if (avatarUrl && !/^(?:https?:|data:|blob:|file:)/i.test(avatarUrl)) {
      avatarUrl = `${IMAGE_BASE_URL}${avatarUrl.replace(/^\/?/, "")}`;
    }
    setPhotoPreview(avatarUrl || "");
    setPhotoFile(null);
    setPhotoChanged(false);
  }, [editOpen]);

  const saveLayout = (layout) => {
    setSelectedLayout(layout);
    localStorage.setItem(POS_PREFERENCE_KEYS.categoryLayout, layout);
  };

  const handleProductImagesToggle = (value) => {
    setShowProductImages(value);
    writePOSDisplayPreference(POS_SHOW_PRODUCT_IMAGES_KEY, value);
  };

  const handleProductPricesToggle = (value) => {
    setShowProductPrices(value);
    writePOSDisplayPreference(POS_SHOW_PRODUCT_PRICES_KEY, value);
  };

  const handleProductQuantityButtonsToggle = (value) => {
    setShowProductQuantityButtons(value);
    writePOSDisplayPreference(POS_SHOW_PRODUCT_QUANTITY_BUTTONS_KEY, value);
  };

  const handleProductQuantityFieldToggle = (value) => {
    setShowProductQuantityField(value);
    writePOSDisplayPreference(POS_SHOW_PRODUCT_QUANTITY_FIELD_KEY, value);
  };

  const handleDeliveryToggle = (value) => {
    setDeliveryEnabled(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.deliveryEnabled,
      value ? "true" : "false"
    );
  };

  const handleTaxBreakupToggle = (value) => {
    setShowTaxBreakup(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.taxBreakupOnBill,
      value ? "true" : "false"
    );
  };

  const handleDeliveryLogoToggle = (value) => {
    setShowDeliveryLogo(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.deliveryCopyLogoEnabled,
      value ? "true" : "false"
    );
  };

  const handleCustomerCopyKotNumberToggle = (value) => {
    setCustomerCopyKotNumber(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.customerCopyKotNumber,
      value ? "true" : "false"
    );
  };

  const handleQrOrderAlertsToggle = (value) => {
    setQrOrderAlertsEnabled(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.qrOrderAlertsEnabled,
      value ? "true" : "false"
    );
    window.dispatchEvent(new Event("qr-alert-settings-changed"));
  };

  const handleOrderCreatedToneToggle = (value) => {
    setOrderCreatedToneEnabled(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.orderCreatedToneEnabled,
      value ? "true" : "false"
    );
  };

  const handleSendWhatsAppBillingOnCreateOrderToggle = (value) => {
    setSendWhatsAppBillingOnCreateOrder(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.sendWhatsAppBillingOnCreateOrder,
      value ? "true" : "false"
    );
  };

  const handleHoldDockToggle = (value) => {
    setHoldDockEnabled(value);
    writeHoldDockPreference(value);
  };

  const handleAiManagerToggle = (value) => {
    setAiManagerEnabled(value);
    writeAiManagerPreference(value);
  };

  const handleDailyOrderSequenceResetToggle = async (value) => {
    if (savingOrderSequenceSetting) return;
    const session = JSON.parse(localStorage.getItem("userData") || "{}");
    const profile = session?.data || session || {};
    const restaurantId = Number(profile?.id || profile?.restaurant_id || 0);
    if (!restaurantId) return window.alert("Restaurant information is missing. Please login again.");

    const previous = dailyOrderSequenceResetEnabled;
    const enabled = Boolean(value);
    setDailyOrderSequenceResetEnabled(enabled);
    setSavingOrderSequenceSetting(true);
    try {
      if (isFullOfflineModeEnabled()) {
        await saveOfflineProfile(restaurantId, {
          daily_order_sequence_reset_enabled: enabled ? 1 : 0,
        });
        writeSequenceSetting(
          session,
          profile,
          restaurantId,
          "daily_order_sequence_reset_enabled",
          enabled
        );
        return;
      }
      const response = await fetch(ORDER_SEQUENCE_SETTINGS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: restaurantId,
          daily_order_sequence_reset_enabled: enabled ? 1 : 0,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Unable to update bill sequence setting.");
      }

      writeSequenceSetting(
        session,
        profile,
        restaurantId,
        "daily_order_sequence_reset_enabled",
        enabled
      );
    } catch (error) {
      setDailyOrderSequenceResetEnabled(previous);
      window.alert(error?.message || "Unable to update bill sequence setting.");
    } finally {
      setSavingOrderSequenceSetting(false);
    }
  };

  const handleDailyKotSequenceResetToggle = async (value) => {
    if (savingKotSequenceSetting) return;
    const session = JSON.parse(localStorage.getItem("userData") || "{}");
    const profile = session?.data || session || {};
    const restaurantId = Number(profile?.id || profile?.restaurant_id || 0);
    if (!restaurantId) return window.alert("Restaurant information is missing. Please login again.");

    const previous = dailyKotSequenceResetEnabled;
    const enabled = Boolean(value);
    setDailyKotSequenceResetEnabled(enabled);
    setSavingKotSequenceSetting(true);
    try {
      if (isFullOfflineModeEnabled()) {
        await saveOfflineProfile(restaurantId, {
          daily_kot_sequence_reset_enabled: enabled ? 1 : 0,
        });
        writeSequenceSetting(
          session,
          profile,
          restaurantId,
          "daily_kot_sequence_reset_enabled",
          enabled
        );
        return;
      }
      const response = await fetch(ORDER_SEQUENCE_SETTINGS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: restaurantId,
          daily_kot_sequence_reset_enabled: enabled ? 1 : 0,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Unable to update KOT sequence setting.");
      }

      writeSequenceSetting(
        session,
        profile,
        restaurantId,
        "daily_kot_sequence_reset_enabled",
        enabled
      );
    } catch (error) {
      setDailyKotSequenceResetEnabled(previous);
      window.alert(error?.message || "Unable to update KOT sequence setting.");
    } finally {
      setSavingKotSequenceSetting(false);
    }
  };

  const handleDeliveryChargeChange = (text) => {
    const normalized = normalizeMoneyInput(text);
    setDeliveryCharge(normalized);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.deliveryCharge,
      normalized || "0"
    );
  };

  const handleDeliveryChargeBlur = () => {
    const normalized = normalizeMoneyInput(deliveryCharge);
    const finalValue = normalized || "0";
    setDeliveryCharge(finalValue);
    localStorage.setItem(POS_PREFERENCE_KEYS.deliveryCharge, finalValue);
  };

  const cropImageToSquare = (file, size = 250) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const side = Math.min(img.width, img.height);
        const sx = Math.max(0, (img.width - side) / 2);
        const sy = Math.max(0, (img.height - side) / 2);
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Image crop failed"));
              return;
            }
            const cropped = new File([blob], file.name || "avatar.jpg", {
              type: blob.type || file.type || "image/jpeg",
            });
            resolve(cropped);
          },
          "image/jpeg",
          0.9
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const cropped = await cropImageToSquare(f, 250);
      setPhotoFile(cropped);
      setPhotoPreview(URL.createObjectURL(cropped));
      setPhotoChanged(true);
    } catch (err) {
      setProfileError(err?.message || "Unable to process image");
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview("");
    setPhotoChanged(true);
  };

  const saveProfile = async () => {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const uid = stored?.data?.id || stored?.id;
    if (!uid) {
      setProfileError("User not found in storage");
      return;
    }
    setSavingProfile(true);
    setProfileError("");
    try {
      const profileChanges = {
        restaurant_name: pShopName,
        name: pName,
        email: pEmail,
        addressline1: pAddr1,
        addressline2: pAddr2,
        addressline3: pAddr3,
        issoundenable: pSound,
        isvibrationenable: pVibration,
        customfield1: pCf1,
        customfield2: pCf2,
        customfield3: pCf3,
        gstno: pGst,
        upiaddress: pUpi,
        showlogoonprinter: pShowLogo,
        showqrcodeonprinter: pShowQr,
        showphonenumberonprinter: pShowPhone,
        showthanksonbill: pShowThanks,
      };
      if (isFullOfflineModeEnabled()) {
        if (photoChanged) {
          const localPhoto = photoFile instanceof File
            ? await readProfileImage(photoFile)
            : "";
          profileChanges.photo = localPhoto;
          profileChanges.avatar_url = localPhoto;
          profileChanges.restaurant_logo = localPhoto;
        }
        const result = await saveOfflineProfile(uid, profileChanges);
        const previous = JSON.parse(localStorage.getItem("userData") || "{}");
        const updatedUser = {
          ...(previous?.data || previous || {}),
          ...(result?.profile || profileChanges),
          id: previous?.data?.id || previous?.id || uid,
        };
        writeStoredSession(
          previous?.data
            ? { ...previous, success: true, data: updatedUser }
            : { success: true, data: updatedUser },
          { preserveAccountLinkContext: true, syncLogRocket: false }
        );
        setEditOpen(false);
        return;
      }
      const form = new FormData();
      form.append("restaurantid", String(uid));
      form.append("restaurant_name", pShopName ?? "");
      form.append("name", pName ?? "");
      form.append("email", pEmail ?? "");
      form.append("addressline1", pAddr1 ?? "");
      form.append("addressline2", pAddr2 ?? "");
      form.append("addressline3", pAddr3 ?? "");
      form.append("issoundenable", String(pSound ?? 0));
      form.append("isvibrationenable", String(pVibration ?? 0));
      form.append("customfield1", pCf1 ?? "");
      form.append("customfield2", pCf2 ?? "");
      form.append("customfield3", pCf3 ?? "");
      form.append("gstno", pGst ?? "");
      form.append("upiaddress", pUpi ?? "");
      form.append("showlogoonprinter", String(pShowLogo ?? 0));
      form.append("showqrcodeonprinter", String(pShowQr ?? 0));
      form.append("showphonenumberonprinter", String(pShowPhone ?? 0));
      form.append("showthanksonbill", String(pShowThanks ?? 0));
      if (photoChanged && photoFile instanceof File) {
        form.append("photo", photoFile, photoFile.name || "avatar.jpg");
      }

      const resp = await fetch(
        API_ENDPOINTS.EDIT_PROFILE,
        {
          method: "POST",
          body: form,
        }
      );
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || result?.success === false) {
        throw new Error(result?.message || `HTTP ${resp.status}`);
      }

      const prev = JSON.parse(localStorage.getItem("userData") || "{}");
      const prevData = prev?.data || {};
      const updatedUser = result?.data
        ? { ...prevData, ...(result.data || {}) }
        : {
            ...prevData,
            restaurant_name: pShopName,
            name: pName,
            email: pEmail,
            addressline1: pAddr1,
            addressline2: pAddr2,
            addressline3: pAddr3,
            issoundenable: pSound,
            isvibrationenable: pVibration,
            customfield1: pCf1,
            customfield2: pCf2,
            customfield3: pCf3,
            gstno: pGst,
            upiaddress: pUpi,
            showlogoonprinter: pShowLogo,
            showqrcodeonprinter: pShowQr,
            showphonenumberonprinter: pShowPhone,
            showthanksonbill: pShowThanks,
          };

      writeStoredSession(
        { ...(prev || {}), success: true, data: updatedUser },
        { preserveAccountLinkContext: true }
      );
      setEditOpen(false);
    } catch (err) {
      setProfileError(err?.message || "Error updating profile");
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div style={sx.page}>
      <div style={sx.header}>
        <div>
          <div style={sx.title}>Printer / POS Settings</div>
          <div style={sx.subtitle}>Control printing, POS layout, and billing preferences</div>
        </div>
        <div style={sx.headerBadge}>⚙️</div>
      </div>

      <div style={sx.autoSaveBanner}>
        <span style={sx.autoSaveIcon}>✓</span>
        <div>
          <div style={sx.autoSaveTitle}>Auto-save is on</div>
          <div style={sx.autoSaveText}>POS and printer preferences are saved as soon as you change them.</div>
        </div>
      </div>

      <PrinterSettingsPage embedded>

      <div style={sx.groupHeading}>POS &amp; ORDER FLOW</div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Category Layout</div>
        <div style={sx.sectionDesc}>
          Choose how categories appear on the order screen.
        </div>
        <div style={sx.optionList}>
          {layoutOptions.map((option) => {
            const isSelected = selectedLayout === option.id;
            return (
              <button
                key={option.id}
                style={{
                  ...sx.optionCard,
                  ...(isSelected ? sx.optionCardActive : null),
                }}
                onClick={() => saveLayout(option.id)}
              >
                <div
                  style={{
                    ...sx.optionIcon,
                    ...(isSelected ? sx.optionIconActive : null),
                  }}
                >
                  {option.icon}
                </div>
                <div style={sx.optionText}>
                  <div style={sx.optionTitle}>{option.title}</div>
                  <div style={sx.optionDesc}>{option.description}</div>
                </div>
                <div style={{ ...sx.radio, ...(isSelected ? sx.radioOn : sx.radioOff) }}>
                  {isSelected ? "✓" : ""}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Product Card Display</div>
        <div style={sx.sectionDesc}>
          Choose what appears on product cards while taking an order. Both options are on by default.
        </div>
        <div style={sx.rowBetween}>
          <div>
            <div style={sx.rowLabel}>Show product pictures</div>
            <div style={sx.rowHint}>Turn off to avoid loading product images on the POS screen.</div>
          </div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={showProductImages}
              onChange={(e) => handleProductImagesToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
        <div style={{ ...sx.rowBetween, marginBottom: 0 }}>
          <div>
            <div style={sx.rowLabel}>Show product prices</div>
            <div style={sx.rowHint}>Turn off to keep prices hidden on POS product cards.</div>
          </div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={showProductPrices}
              onChange={(e) => handleProductPricesToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
        <div style={{ ...sx.displayDivider }} />
        <div style={sx.rowBetween}>
          <div>
            <div style={sx.rowLabel}>Show + / − buttons</div>
            <div style={sx.rowHint}>Show quantity increase and decrease buttons on product cards.</div>
          </div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={showProductQuantityButtons}
              onChange={(e) => handleProductQuantityButtonsToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
        <div style={{ ...sx.rowBetween, marginBottom: 0 }}>
          <div>
            <div style={sx.rowLabel}>Show quantity field</div>
            <div style={sx.rowHint}>Show the editable quantity number between the buttons.</div>
          </div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={showProductQuantityField}
              onChange={(e) => handleProductQuantityFieldToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Order Hold Controls</div>
        <div style={sx.sectionDesc}>
          Show the Hold action and occupied-orders list on the Order Screen. Enabled by default.
        </div>
        <div style={{ ...sx.rowBetween, marginBottom: 0 }}>
          <div>
            <div style={sx.rowLabel}>Show Hold buttons</div>
            <div style={sx.rowHint}>Turn off to hide the summary Hold action and held-orders shortcut.</div>
          </div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              aria-label="Show Hold buttons on Order Screen"
              checked={holdDockEnabled}
              onChange={(e) => handleHoldDockToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>AI Manager</div>
        <div style={sx.sectionDesc}>
          Show the AI Manager on the Order Screen. Disabled by default.
        </div>
        <div style={{ ...sx.rowBetween, marginBottom: 0 }}>
          <div>
            <div style={sx.rowLabel}>Enable AI Manager</div>
            <div style={sx.rowHint}>You can also hide it using the × button beside its collapsed launcher.</div>
          </div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              aria-label="Enable AI Manager on Order Screen"
              checked={aiManagerEnabled}
              onChange={(e) => handleAiManagerToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Delivery Settings</div>
        <div style={sx.sectionDesc}>
          Enable delivery charges and set a default amount.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Enable Delivery</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={deliveryEnabled}
              onChange={(e) => handleDeliveryToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
        <div
          style={{
            ...sx.inputWrap,
            ...(deliveryEnabled ? null : sx.inputWrapDisabled),
          }}
        >
          <span style={sx.currency}>{CURRENCY_SYMBOL}</span>
          <input
            style={sx.input}
            placeholder="Default delivery charge"
            value={deliveryCharge}
            onChange={(e) => handleDeliveryChargeChange(e.target.value)}
            onBlur={handleDeliveryChargeBlur}
            disabled={!deliveryEnabled}
          />
        </div>
        <div style={sx.helperText}>
          This amount will be prefilled on the Order Screen.
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Delivery Copy Logo</div>
        <div style={sx.sectionDesc}>
          Show delivery logo on delivery copy print.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Enable delivery logo</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={showDeliveryLogo}
              onChange={(e) => handleDeliveryLogoToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Tax Breakup on Bill</div>
        <div style={sx.sectionDesc}>
          Show {TAX_LABEL} breakup on printed bills.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Show tax breakup</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={showTaxBreakup}
              onChange={(e) => handleTaxBreakupToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Customer Copy KOT Number</div>
        <div style={sx.sectionDesc}>
          Print the actual KOT number above the logo on the Multiple Receipts Customer Copy.
        </div>
        <div style={sx.rowBetween}>
          <div>
            <div style={sx.rowLabel}>Add KOT number in Customer Copy</div>
            <div style={sx.rowHint}>Multiple Receipts creates or reuses the real KOT number; the order number is never substituted.</div>
          </div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              aria-label="Add KOT number in Customer Copy"
              checked={customerCopyKotNumber}
              onChange={(e) => handleCustomerCopyKotNumberToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Bill Number Sequence</div>
        <div style={sx.sectionDesc}>
          Choose whether bill numbers continue forever or restart from 00001 every day at 12:00 AM.
        </div>
        <div style={sx.optionList}>
          {[
            {
              enabled: false,
              title: "Continuous Sequence",
              description: "Bill numbers keep increasing across days.",
              icon: "∞",
            },
            {
              enabled: true,
              title: "Restart Daily from 00001",
              description: "Every new business day starts again from 00001.",
              icon: "01",
            },
          ].map((option) => {
            const active = dailyOrderSequenceResetEnabled === option.enabled;
            return (
              <button
                key={String(option.enabled)}
                type="button"
                disabled={savingOrderSequenceSetting}
                style={{
                  ...sx.optionCard,
                  background: active
                    ? "linear-gradient(135deg, var(--app-primary), var(--app-primary-dark))"
                    : "#ffffff",
                  border: active ? "2px solid var(--app-primary)" : "1px solid #cbd5e1",
                  boxShadow: active
                    ? "0 10px 24px rgba(var(--app-primary-rgb), .22)"
                    : "none",
                  opacity: savingOrderSequenceSetting ? 0.72 : 1,
                }}
                onClick={() => handleDailyOrderSequenceResetToggle(option.enabled)}
              >
                <div style={{
                  ...sx.optionIcon,
                  background: active ? "#ffffff" : "var(--app-primary-soft)",
                  color: "var(--app-primary)",
                }}>
                  {option.icon}
                </div>
                <div style={sx.optionText}>
                  <div style={{ ...sx.optionTitle, color: active ? "#ffffff" : "#0f172a" }}>
                    {option.title}
                  </div>
                  <div style={{ ...sx.optionDesc, color: active ? "var(--app-primary-soft)" : "#64748b" }}>
                    {option.description}
                  </div>
                </div>
                <div style={{
                  ...sx.sequenceState,
                  background: active ? "#ffffff" : "#f1f5f9",
                  color: active ? "var(--app-primary)" : "#64748b",
                  borderColor: active ? "#ffffff" : "#cbd5e1",
                }}>
                  {active ? "✓ SELECTED" : "SELECT"}
                </div>
              </button>
            );
          })}
        </div>
        <div style={sx.helperText}>
          {savingOrderSequenceSetting
            ? "Saving bill sequence preference…"
            : "This setting is shared with the mobile app and applies to the next generated bill number."}
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>KOT Number Sequence</div>
        <div style={sx.sectionDesc}>
          Restart KOT sequence daily at 12:00 AM or keep it continuous across days.
        </div>
        <div style={sx.optionList}>
          {[
            {
              enabled: false,
              title: "Continuous KOT Sequence",
              description: "KOT numbers keep increasing continuously across days.",
              icon: "∞",
            },
            {
              enabled: true,
              title: "Restart Daily at 12:00 AM",
              description: "Resets the backend KOT sequence after midnight every business day.",
              icon: "01",
            },
          ].map((option) => {
            const active = dailyKotSequenceResetEnabled === option.enabled;
            return (
              <button
                key={String(option.enabled)}
                type="button"
                disabled={savingKotSequenceSetting}
                style={{
                  ...sx.optionCard,
                  background: active
                    ? "linear-gradient(135deg, var(--app-primary), var(--app-primary-dark))"
                    : "#ffffff",
                  border: active ? "2px solid var(--app-primary)" : "1px solid #cbd5e1",
                  boxShadow: active
                    ? "0 10px 24px rgba(var(--app-primary-rgb), .22)"
                    : "none",
                  opacity: savingKotSequenceSetting ? 0.72 : 1,
                }}
                onClick={() => handleDailyKotSequenceResetToggle(option.enabled)}
              >
                <div style={{
                  ...sx.optionIcon,
                  background: active ? "#ffffff" : "var(--app-primary-soft)",
                  color: "var(--app-primary)",
                }}>
                  {option.icon}
                </div>
                <div style={sx.optionText}>
                  <div style={{ ...sx.optionTitle, color: active ? "#ffffff" : "#0f172a" }}>
                    {option.title}
                  </div>
                  <div style={{ ...sx.optionDesc, color: active ? "var(--app-primary-soft)" : "#64748b" }}>
                    {option.description}
                  </div>
                </div>
                <div style={{
                  ...sx.sequenceState,
                  background: active ? "#ffffff" : "#f1f5f9",
                  color: active ? "var(--app-primary)" : "#64748b",
                  borderColor: active ? "#ffffff" : "#cbd5e1",
                }}>
                  {active ? "✓ SELECTED" : "SELECT"}
                </div>
              </button>
            );
          })}
        </div>
        <div style={sx.helperText}>
          {savingKotSequenceSetting
            ? "Saving KOT sequence setting…"
            : "This applies to the whole restaurant and resets the backend KOT sequence after midnight."}
        </div>
      </div>

      <div style={sx.groupHeading}>ALERTS &amp; AUTOMATION</div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>QR Order Alerts</div>
        <div style={sx.sectionDesc}>
          Keep QR order bell, sound and popup alerts enabled.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Enable QR alerts</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={qrOrderAlertsEnabled}
              onChange={(e) => handleQrOrderAlertsToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Order Created Tone</div>
        <div style={sx.sectionDesc}>
          Play a short success tone when a new order is created from Order Screen.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Enable order success tone</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={orderCreatedToneEnabled}
              onChange={(e) => handleOrderCreatedToneToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Auto WhatsApp Billing</div>
        <div style={sx.sectionDesc}>
          Automatically send invoice on WhatsApp when a new order is created for a selected customer.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Send WhatsApp billing on create order</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={sendWhatsAppBillingOnCreateOrder}
              onChange={(e) =>
                handleSendWhatsAppBillingOnCreateOrderToggle(e.target.checked)
              }
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.groupHeading}>SECURITY</div>

      <div style={{ ...sx.section, padding: 0, overflow: "hidden" }}>
        <div className="pos-lock-heading">
          <div className="pos-lock-icon">🔐</div>
          <div>
            <div style={sx.sectionTitle}>POS Screen Lock PIN</div>
            <div style={{ ...sx.sectionDesc, marginBottom: 0 }}>
              Create a 4–6 digit PIN to prevent unauthorized POS actions.
            </div>
          </div>
          <div className={`pos-lock-status ${lockPinConfigured ? "active" : "inactive"}`}>
            {lockPinConfigured ? "PIN ACTIVE" : "NOT SET"}
          </div>
        </div>
        <div className="pos-lock-body">
          <div className="pos-lock-help">Once locked, the current screen remains visible but every action is blocked until the correct PIN is entered.</div>
          <div className="pos-lock-fields">
            {lockPinConfigured ? <label className="pos-lock-field">
              <span>Current PIN</span>
              <input type="password" inputMode="numeric" maxLength={6} value={currentLockPin} onChange={(event) => { setCurrentLockPin(cleanPin(event.target.value)); setLockPinMessage(""); }} placeholder="Enter current PIN" />
            </label> : null}
            <label className="pos-lock-field">
              <span>{lockPinConfigured ? "New PIN" : "Create PIN"}</span>
              <input type="password" inputMode="numeric" maxLength={6} value={newLockPin} onChange={(event) => { setNewLockPin(cleanPin(event.target.value)); setLockPinMessage(""); }} placeholder="Enter 4–6 digits" />
            </label>
            <label className="pos-lock-field">
              <span>Confirm PIN</span>
              <input type="password" inputMode="numeric" maxLength={6} value={confirmLockPin} onChange={(event) => { setConfirmLockPin(cleanPin(event.target.value)); setLockPinMessage(""); }} placeholder="Re-enter PIN" />
            </label>
          </div>
          {lockPinMessage ? <div className={`pos-lock-message ${lockPinMessage.includes("success") || lockPinMessage.includes("removed") ? "success" : "error"}`}>{lockPinMessage}</div> : null}
          <div className="pos-lock-actions">
            <button type="button" className="pos-lock-save" onClick={() => void handleSaveLockPin()} disabled={savingLockPin}>
              {savingLockPin ? "Saving…" : lockPinConfigured ? "Change PIN" : "Set PIN"}
            </button>
            {lockPinConfigured ? <button type="button" className="pos-lock-remove" onClick={() => void handleRemoveLockPin()}>
              Remove PIN
            </button> : null}
          </div>
        </div>
      </div>

      <div style={sx.groupHeading}>RESTAURANT PROFILE</div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Restaurant Profile</div>
        <div style={sx.sectionDesc}>
          Edit restaurant details, printer toggles and logo.
        </div>
        <button style={sx.primaryBtn} onClick={() => setEditOpen(true)}>
          Edit Profile
        </button>
      </div>

      <div style={sx.groupHeading}>PRINTER OUTPUT</div>

      </PrinterSettingsPage>

      {editOpen ? (
        <div style={sx.modalBackdrop} onClick={() => setEditOpen(false)}>
          <div style={sx.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={sx.modalHeader}>
              <div style={sx.modalTitle}>Edit Profile</div>
              <button style={sx.modalClose} onClick={() => setEditOpen(false)}>
                ✕
              </button>
            </div>
            {profileError ? <div style={sx.modalError}>{profileError}</div> : null}

            <div style={sx.modalGrid}>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Restaurant Name</label>
                <input style={sx.modalInput} value={pShopName} onChange={(e) => setPShopName(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Owner Name</label>
                <input style={sx.modalInput} value={pName} onChange={(e) => setPName(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Email</label>
                <input style={sx.modalInput} value={pEmail} onChange={(e) => setPEmail(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Address Line 1</label>
                <input style={sx.modalInput} value={pAddr1} onChange={(e) => setPAddr1(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Address Line 2</label>
                <input style={sx.modalInput} value={pAddr2} onChange={(e) => setPAddr2(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Address Line 3</label>
                <input style={sx.modalInput} value={pAddr3} onChange={(e) => setPAddr3(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Custom Field 1</label>
                <input style={sx.modalInput} value={pCf1} onChange={(e) => setPCf1(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Custom Field 2</label>
                <input style={sx.modalInput} value={pCf2} onChange={(e) => setPCf2(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Custom Field 3</label>
                <input style={sx.modalInput} value={pCf3} onChange={(e) => setPCf3(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>{TAX_REGISTRATION_LABEL}</label>
                <input style={sx.modalInput} value={pGst} onChange={(e) => setPGst(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>UPI Address</label>
                <input style={sx.modalInput} value={pUpi} onChange={(e) => setPUpi(e.target.value)} />
              </div>
            </div>

            <div style={sx.photoRow}>
              <div>
                <div style={sx.modalLabel}>Logo</div>
                {photoPreview ? (
                  <img src={photoPreview} alt="logo" style={sx.photoPreview} />
                ) : (
                  <div style={sx.photoPlaceholder}>No logo</div>
                )}
              </div>
              <div style={sx.photoActions}>
                <button style={sx.secondaryBtn} onClick={() => fileInputRef.current?.click()}>
                  Upload & Crop
                </button>
                <button style={sx.ghostBtn} onClick={removePhoto}>
                  Remove
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onPickPhoto}
                />
              </div>
            </div>

            <div style={sx.toggleGrid}>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pSound} onChange={(e) => setPSound(e.target.checked ? 1 : 0)} />
                Sound
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pVibration} onChange={(e) => setPVibration(e.target.checked ? 1 : 0)} />
                Vibration
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pShowLogo} onChange={(e) => setPShowLogo(e.target.checked ? 1 : 0)} />
                Show Logo on Bill
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pShowQr} onChange={(e) => setPShowQr(e.target.checked ? 1 : 0)} />
                Show QR on Bill
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pShowPhone} onChange={(e) => setPShowPhone(e.target.checked ? 1 : 0)} />
                Show Phone on Bill
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pShowThanks} onChange={(e) => setPShowThanks(e.target.checked ? 1 : 0)} />
                Show Thanks on Bill
              </label>
            </div>

            <div style={sx.modalActions}>
              <button style={sx.ghostBtn} onClick={() => setEditOpen(false)}>
                Cancel
              </button>
              <button style={sx.primaryBtn} onClick={saveProfile} disabled={savingProfile}>
                {savingProfile ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const sx = {
  page: {
    minHeight: "100vh",
    background: "#f6f8fb",
    padding: 16,
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
    color: "#0f172a",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(135deg, var(--app-primary), var(--app-primary-dark))",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: 16,
    boxShadow: "0 10px 24px rgba(var(--app-primary-rgb), .18)",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 800 },
  subtitle: { fontSize: 12, opacity: 0.9, marginTop: 4 },
  headerBadge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "rgba(255,255,255,0.2)",
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
  },
  autoSaveBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "11px 14px",
    marginBottom: 16,
    borderRadius: 12,
    border: "1px solid #bbf7d0",
    background: "#f0fdf4",
  },
  autoSaveIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    display: "grid",
    placeItems: "center",
    background: "#16a34a",
    color: "#fff",
    fontWeight: 900,
  },
  autoSaveTitle: { color: "#166534", fontSize: 13, fontWeight: 800 },
  autoSaveText: { color: "#475569", fontSize: 12, marginTop: 2 },
  section: {
    background: "#fff",
    padding: 16,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    marginBottom: 14,
    boxShadow: "0 6px 20px rgba(15,23,42,.05)",
  },
  groupHeading: {
    margin: "22px 4px 9px",
    color: "#475569",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.2,
  },
  sectionTitle: { fontSize: 16, fontWeight: 800, marginBottom: 6 },
  sectionDesc: { color: "#64748b", marginBottom: 12, fontSize: 13 },
  optionList: { display: "grid", gap: 12 },
  optionCard: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: 14,
    borderRadius: 12,
    border: "2px solid #e2e8f0",
    background: "#f8fafc",
    textAlign: "left",
    cursor: "pointer",
  },
  optionCardActive: {
    borderColor: BRAND,
    background: "var(--app-primary-soft)",
  },
  optionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    background: LIGHT,
    display: "grid",
    placeItems: "center",
    fontSize: 20,
    color: BRAND,
  },
  optionIconActive: {
    background: BRAND,
    color: "#fff",
  },
  optionText: { flex: 1 },
  optionTitle: { fontWeight: 700, marginBottom: 4 },
  optionDesc: { fontSize: 12, color: "#64748b" },
  radio: {
    width: 24,
    height: 24,
    borderRadius: 12,
    border: "2px solid #cbd5e1",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
  },
  radioOn: { background: BRAND, color: "#fff", borderColor: BRAND },
  radioOff: { background: "#fff", color: "transparent" },
  sequenceState: {
    minWidth: 82,
    padding: "7px 10px",
    borderRadius: 999,
    border: "1px solid",
    textAlign: "center",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: ".05em",
    whiteSpace: "nowrap",
  },

  rowBetween: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  rowLabel: { fontWeight: 700 },
  rowHint: { color: "#64748b", fontSize: 12, marginTop: 3 },
  displayDivider: { height: 1, background: "#e2e8f0", margin: "14px 0" },

  inputWrap: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "8px 12px",
    background: "#fff",
  },
  inputWrapDisabled: { opacity: 0.6 },
  currency: { fontWeight: 700, color: BRAND, marginRight: 6 },
  input: {
    border: "none",
    outline: "none",
    flex: 1,
    fontSize: 14,
    color: "#0f172a",
    background: "transparent",
  },
  helperText: { fontSize: 12, color: "#64748b", marginTop: 8 },

  optionRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  chipBtn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "2px solid #e2e8f0",
    background: "#f8fafc",
    fontWeight: 700,
    cursor: "pointer",
  },
  chipBtnActive: {
    borderColor: BRAND,
    background: "var(--app-primary-soft)",
    color: BRAND,
  },
  subSectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    marginTop: 8,
    marginBottom: 6,
  },

  toggle: {
    position: "relative",
    width: 42,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
  },
  primaryBtn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "none",
    background: BRAND,
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "9px 12px",
    borderRadius: 10,
    border: `1px solid ${BRAND}`,
    background: "var(--app-primary-soft)",
    color: BRAND,
    fontWeight: 700,
    cursor: "pointer",
  },
  ghostBtn: {
    padding: "9px 12px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 999,
  },
  modalCard: {
    width: "min(900px, 100%)",
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 24px 40px rgba(15,23,42,.25)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  modalTitle: { fontWeight: 800, fontSize: 16 },
  modalClose: {
    border: "none",
    background: "transparent",
    fontSize: 18,
    cursor: "pointer",
  },
  modalError: {
    color: "#b91c1c",
    fontWeight: 700,
    marginBottom: 8,
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 10,
    marginBottom: 12,
  },
  modalField: { display: "grid", gap: 6 },
  modalLabel: { fontSize: 12, color: "#6b7280", fontWeight: 700 },
  modalInput: {
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  photoRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: 12,
  },
  photoPreview: {
    width: 90,
    height: 90,
    objectFit: "cover",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
  },
  photoPlaceholder: {
    width: 90,
    height: 90,
    borderRadius: 12,
    border: "1px dashed #e2e8f0",
    display: "grid",
    placeItems: "center",
    color: "#94a3b8",
    fontSize: 12,
  },
  photoActions: { display: "flex", gap: 8, alignItems: "center" },
  toggleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 8,
    marginBottom: 12,
  },
  toggleRow: { display: "flex", alignItems: "center", gap: 8, fontWeight: 600 },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
};

const styles = document.createElement("style");
styles.innerHTML = `
  .pos-toggle input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  .pos-toggle-slider {
    position: absolute;
    inset: 0;
    background: #cbd5e1;
    border-radius: 999px;
    transition: background 0.2s ease;
  }
  .pos-toggle-slider::before {
    content: "";
    position: absolute;
    height: 18px;
    width: 18px;
    left: 3px;
    top: 3px;
    background: #f1f5f9;
    border-radius: 50%;
    transition: transform 0.2s ease, background 0.2s ease;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.25);
  }
  .pos-toggle input:checked + .pos-toggle-slider {
    background: #cceaff;
  }
  .pos-toggle input:checked + .pos-toggle-slider::before {
    transform: translateX(18px);
    background: var(--app-primary);
  }
  .pos-lock-heading { display:flex;align-items:center;gap:13px;padding:17px 18px;background:linear-gradient(135deg,#f8fbff,var(--app-primary-soft));border-bottom:1px solid var(--app-primary-soft); }
  .pos-lock-icon { width:44px;height:44px;flex:0 0 44px;display:grid;place-items:center;border-radius:13px;background:#fff;border:1px solid var(--app-primary-soft);font-size:21px;box-shadow:0 4px 12px rgba(var(--app-primary-rgb), .08); }
  .pos-lock-status { margin-left:auto;padding:6px 9px;border-radius:999px;font-size:10px;font-weight:900;letter-spacing:.06em;white-space:nowrap; }
  .pos-lock-status.active { color:#047857;background:#ecfdf5;border:1px solid #a7f3d0; }
  .pos-lock-status.inactive { color:#64748b;background:#fff;border:1px solid #cbd5e1; }
  .pos-lock-body { padding:18px; }
  .pos-lock-help { padding:10px 12px;margin-bottom:14px;border-radius:10px;background:#f8fafc;border:1px solid #e2e8f0;color:#64748b;font-size:12px;line-height:1.45; }
  .pos-lock-fields { display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;max-width:720px; }
  .pos-lock-field { display:grid;gap:6px;color:#334155;font-size:12px;font-weight:800; }
  .pos-lock-field input { width:100%;box-sizing:border-box;padding:11px 12px;border:1px solid #cbd5e1;border-radius:10px;background:#fff;color:#0f172a;font-size:15px;letter-spacing:.08em;outline:none; }
  .pos-lock-field input:focus { border-color:var(--app-primary-hover);box-shadow:0 0 0 3px rgba(7,134,197,.12); }
  .pos-lock-message { max-width:696px;margin-top:11px;padding:9px 11px;border-radius:9px;font-size:12px;font-weight:800; }
  .pos-lock-message.error { color:#b91c1c;background:#fef2f2;border:1px solid #fecaca; }
  .pos-lock-message.success { color:#047857;background:#ecfdf5;border:1px solid #a7f3d0; }
  .pos-lock-actions { display:flex;gap:9px;flex-wrap:wrap;margin-top:14px; }
  .pos-lock-save,.pos-lock-remove { min-width:120px;padding:10px 15px;border-radius:10px;font-weight:800;cursor:pointer; }
  .pos-lock-save { border:0;background:linear-gradient(135deg,var(--app-primary),var(--app-primary-hover));color:#fff;box-shadow:0 5px 13px rgba(var(--app-primary-rgb), .16); }
  .pos-lock-save:disabled { opacity:.6;cursor:not-allowed; }
  .pos-lock-remove { border:1px solid #fecaca;background:#fff;color:#dc2626; }
  @media (max-width:650px) { .pos-lock-heading{align-items:flex-start;padding:15px}.pos-lock-status{font-size:9px}.pos-lock-body{padding:15px}.pos-lock-fields{grid-template-columns:1fr}.pos-lock-actions button{flex:1}.pos-lock-icon{width:38px;height:38px;flex-basis:38px} }
`;
if (!document.getElementById("pos-settings-style")) {
  styles.id = "pos-settings-style";
  document.head.appendChild(styles);
}

export default POSSettingsPage;
